import { Popover } from "@progress/kendo-react-tooltip";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataPlaneNotificationControllerService } from "@/api";
import TabStripTabs, { TabItem } from "@/components/Common/TabStripTabs";
import useEscapeKey from "@/hooks/useEscapeKey";
import usePopupState from "@/hooks/usePopupState";
import useQuery from "@/hooks/useQuery";
import useReadState, { ReadStateKey } from "@/hooks/useReadState";
import NotificationIcon from "@/layout/NotificationIcon";
import Alerts from "./Alerts";

import "@/styles/shared/dialog-tab-strip.scss";
import "./notifications.scss";

interface NotificationsContentProps {
  /** When false, tab selection resets so reopening the popover starts on Notifications. */
  isPopoverOpen: boolean;
  hasUnreadAlerts: boolean;
  markAlertAsRead: (id: string) => void;
}

function NotificationsContent({
  isPopoverOpen,
  hasUnreadAlerts,
  markAlertAsRead,
}: NotificationsContentProps) {
  // TabStripTabs selected index: 0 = Alerts,
  const [selected, setSelected] = useState<number>(0);

  useEffect(() => {
    if (!isPopoverOpen) {
      setSelected(0);
    }
  }, [isPopoverOpen]);

  const alertsTitle = useMemo(
    () => (
      <>
        Notifications
        {hasUnreadAlerts && <span className="tlx-health-menu--tab-indicator" />}
      </>
    ),
    [hasUnreadAlerts],
  );

  const TabItems: TabItem[] = useMemo(() => {
    const tabs: TabItem[] = [
      {
        key: "alerts",
        title: alertsTitle,
        Component: Alerts,
        componentProps: {
          markAsRead: markAlertAsRead,
        },
      },
    ];

    return tabs;
  }, [alertsTitle, markAlertAsRead]);

  return (
    <div className="tlx-health-menu--popup">
      <TabStripTabs
        tabs={TabItems}
        selectedKey={TabItems[selected]?.key ?? TabItems[0].key}
        onTabChange={(key: string, idx: number) => setSelected(idx)}
        className="tlx-dialog-tab-strip with-border"
      />
    </div>
  );
}

const Notifications: React.FC = () => {
  const anchor = useRef<HTMLDivElement>(null);
  const [isShow, show, hide] = usePopupState(false);
  useEscapeKey(hide);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const targetElement = event.target as HTMLElement;
      if (targetElement.closest(".tlx-health-menu")) return;
      const parentElement = targetElement.closest(
        ".k-animation-container-shown",
      );

      if (!parentElement) {
        hide();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hide]);

  const {
    isRead: isAlertRead,
    markAsRead: markAlertAsRead,
    readItemsSet: alertReadItems,
  } = useReadState(ReadStateKey.ALERTS);

  // Check for unread alerts by fetching alert data
  const { data: alertsData = [] } = useQuery({
    method:
      DataPlaneNotificationControllerService.listDataPlaneNotificationForDashboardUsingGet,
    params: {
      accountId: [],
      pageNo: 1,
      pageSize: 1000,
      sinceDays: 7,
      sortBy: "createdDate",
      sortOrder: "DESC",
      statusValue: "FAILED",
      type: "ALERT",
    },
    queryKey: ["alertsForReadState"],
  });

  const { data: infoData = [] } = useQuery({
    method:
      DataPlaneNotificationControllerService.listDataPlaneNotificationForDashboardUsingGet,
    params: {
      accountId: [],
      pageNo: 1,
      pageSize: 1000,
      sinceDays: 7,
      sortBy: "createdDate",
      sortOrder: "DESC",
      statusValue: "COMPLETED",
      type: "DELTA_SYNC_SERVICE",
    },
    queryKey: ["infoForReadState"],
  });

  const allAlerts = useMemo(
    () => [
      ...(Array.isArray(alertsData) ? alertsData : []),
      ...(Array.isArray(infoData) ? infoData : []),
    ],
    [alertsData, infoData],
  );

  const hasUnreadAlerts = useMemo(() => {
    return (
      alertReadItems && // added this to take affect and run hasUnreadAlerts again when any changes occur in alertReadItems
      allAlerts.some(alert => alert.id && !isAlertRead(alert.id))
    );
  }, [allAlerts, isAlertRead, alertReadItems]);

  const hasAnyUnread = hasUnreadAlerts;

  return (
    <div className="tlx-health-menu">
      <div
        className="tlx-health-menu--holder"
        ref={anchor}
        onClick={() => {
          if (isShow) hide();
          else show();
        }}
      >
        <NotificationIcon hasUnread={hasAnyUnread} />
      </div>
      <Popover
        show={isShow}
        anchor={anchor.current}
        position={"bottom"}
        className="tlx-health-menu--popover"
        margin={{ horizontal: 85, vertical: 15 }}
      >
        <NotificationsContent
          isPopoverOpen={isShow}
          hasUnreadAlerts={hasUnreadAlerts}
          markAlertAsRead={markAlertAsRead}
        />
      </Popover>
    </div>
  );
};

export default Notifications;
